import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useMeta } from "@/context/MetaContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend,
  LineChart, Line, CartesianGrid,
} from "recharts";
import { Loader2, Users, Send, MessageSquareReply, TrendingUp, TrendingDown, AlertOctagon, Info,
  Activity, CheckCircle2, GitBranch, StickyNote, Download, AlertTriangle, X,
  Target, Trophy, IndianRupee, Pencil, ClipboardCheck,
} from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const { meta } = useMeta();
  const { data, isLoading } = useQuery({
    queryKey: ["reports"],
    queryFn: async () => (await api.get("/reports")).data,
  });

  if (isLoading || !data)
    return <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-forest" /></div>;

  const stageLabel = (k) => (meta?.stages || []).find((s) => s.key === k)?.short || k;
  const funnel = [
    { name: "Prospects", value: data.funnel.prospects },
    { name: "Samples", value: data.funnel.reached_samples },
    { name: "Dispatch", value: data.funnel.reached_dispatch },
    { name: "Order stage", value: data.funnel.reached_order },
    { name: "Positive", value: data.funnel.positive_feedback },
    { name: "Orders", value: data.funnel.orders },
  ];
  const stageData = Object.entries(data.stage_counts || {}).map(([k, v]) => ({ name: stageLabel(k), value: v }));
  const COLORS = ["#14532D", "#166534", "#B45309", "#D97706", "#10B981", "#155E75"];

  return (
    <div className="animate-fade-up">
      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900">Reports</h1>
        <p className="mt-1 text-sm text-stone-500">Team performance and pipeline conversion.</p>
      </div>

      <WorkProgress />

      <DailyTasks />

      <DailyReportsView />

      <GoalTargets />

      <Leaderboard />

      <RevenueByExecutive />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
        <Kpi icon={Users} label="Total customers" value={data.total_customers} />
        <Kpi icon={Send} label="Confirmed messages" value={data.confirmed_messages} />
        <Kpi icon={MessageSquareReply} label="Response rate" value={`${data.response_rate}%`} />
        <Kpi icon={AlertOctagon} label="Excessive follow-ups" value={data.excessive_followups} tone="text-red-600" />
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <Card title="Pipeline funnel">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={funnel} margin={{ left: -20 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {funnel.map((e, i) => <Cell key={e.name} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Customers by stage">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stageData} margin={{ left: -20 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#14532D" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        <Conv label="Introduction → Sample" value={data.conversions.intro_to_sample} />
        <Conv label="Sample → Positive feedback" value={data.conversions.sample_to_positive} />
        <Conv label="Positive feedback → Order" value={data.conversions.positive_to_order} />
      </div>

      <Card title="Salesperson performance" className="mt-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="reports-salesperson-table">
            <thead>
              <tr className="border-b border-stone-200 text-left text-xs uppercase tracking-wide text-stone-400">
                <th className="py-2 pr-4">Salesperson</th>
                <th className="py-2 pr-4">Assigned</th>
                <th className="py-2 pr-4">Messages sent</th>
                <th className="py-2 pr-4">Orders</th>
                <th className="py-2">Conversion</th>
              </tr>
            </thead>
            <tbody>
              {data.per_salesperson.map((s) => (
                <tr key={s.name} className="border-b border-stone-100">
                  <td className="py-2.5 pr-4 font-medium text-stone-800">{s.name}</td>
                  <td className="py-2.5 pr-4">{s.assigned}</td>
                  <td className="py-2.5 pr-4">{s.messages_sent}</td>
                  <td className="py-2.5 pr-4">{s.orders}</td>
                  <td className="py-2.5 font-semibold text-forest">{s.conversion}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-5 flex items-start gap-2 rounded-lg border border-stone-200 bg-white p-3 text-xs text-stone-500">
        <Info className="h-4 w-4 shrink-0 text-stone-400" />
        {data.note}
      </div>
    </div>
  );
}

const ACT_META = [
  { key: "messages_sent", label: "Messages sent", color: "#14532D", icon: Send },
  { key: "responses", label: "Responses recorded", color: "#B45309", icon: MessageSquareReply },
  { key: "followups_completed", label: "Follow-ups done", color: "#10B981", icon: CheckCircle2 },
  { key: "stage_changes", label: "Stage changes", color: "#155E75", icon: GitBranch },
  { key: "notes", label: "Notes added", color: "#D97706", icon: StickyNote },
];

function WorkProgress() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("daily");
  const [salesperson, setSalesperson] = useState("all");
  const [customer, setCustomer] = useState("all");
  const [selected, setSelected] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [exporting, setExporting] = useState(false);
  const rangeOn = start && end;

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
  });
  const salespeople = users.filter((u) => u.role === "executive" || u.role === "manager");

  const { data: customers = [] } = useQuery({
    queryKey: ["all-customers-lite"],
    queryFn: async () => (await api.get("/customers", { params: { status: "all" } })).data,
  });

  const baseParams = () => {
    const p = { period };
    if (salesperson !== "all") p.salesperson_id = salesperson;
    if (customer !== "all") p.customer_id = customer;
    if (rangeOn) { p.start = start; p.end = end; }
    return p;
  };

  const { data, isLoading } = useQuery({
    queryKey: ["activity", period, salesperson, customer, start, end],
    queryFn: async () => (await api.get("/reports/activity", { params: baseParams() })).data,
  });

  const totals = data?.totals || {};
  const series = data?.series || [];
  const idle = data?.idle_customers || [];

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ["activity-detail", selected, period, salesperson, customer, start, end],
    enabled: !!selected,
    queryFn: async () => {
      const params = { ...baseParams(), metric: selected };
      return (await api.get("/reports/activity/detail", { params })).data;
    },
  });
  const selectedMeta = ACT_META.find((m) => m.key === selected);
  const fmtWhen = (iso) => {
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return (iso || "").slice(0, 10); }
  };

  const doExport = async (fmt) => {
    setExporting(true);
    try {
      const params = { ...baseParams(), fmt };
      if (selected) params.metric = selected;
      const res = await api.get("/reports/activity/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pgt_work_progress${selected ? "_" + selected : ""}.${fmt === "csv" ? "csv" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-5" data-testid="work-progress-section">
      <div className="mb-4 flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="flex items-center gap-2 font-heading font-semibold text-stone-900">
            <Activity className="h-4 w-4 text-forest" /> Work progress
          </h3>
          <div className="flex flex-wrap gap-2">
            <Select value={salesperson} onValueChange={setSalesperson}>
              <SelectTrigger className="h-9 w-full sm:w-44" data-testid="activity-salesperson-filter"><SelectValue placeholder="Salesperson" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All salespeople</SelectItem>
                {salespeople.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={customer} onValueChange={setCustomer}>
              <SelectTrigger className="h-9 w-full sm:w-48" data-testid="activity-customer-filter"><SelectValue placeholder="Customer" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All customers</SelectItem>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex rounded-lg border border-stone-200 p-0.5" data-testid="activity-period-toggle">
              {["daily", "weekly", "monthly"].map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  data-testid={`activity-period-${p}`}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium capitalize transition-colors ${period === p ? "bg-forest text-ivory" : "text-stone-500 hover:text-forest"}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-stone-100 pt-3">
          <span className="text-xs font-medium text-stone-400">Date range:</span>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-8 rounded-lg border border-stone-200 bg-white px-2 text-xs text-stone-700" data-testid="activity-start-date" />
          <span className="text-xs text-stone-400">to</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-8 rounded-lg border border-stone-200 bg-white px-2 text-xs text-stone-700" data-testid="activity-end-date" />
          {rangeOn && (
            <button onClick={() => { setStart(""); setEnd(""); }} className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-stone-400 hover:text-forest" data-testid="activity-range-clear"><X className="h-3 w-3" /> Clear range</button>
          )}
          {!rangeOn && <span className="text-[11px] text-stone-400">(defaults to rolling {period} window)</span>}
          <div className="ml-auto flex items-center gap-1.5">
            <span className="text-xs font-medium text-stone-400"><Download className="mr-1 inline h-3.5 w-3.5" />Export{selected ? ` (${selectedMeta?.label})` : ""}:</span>
            <button onClick={() => doExport("xlsx")} disabled={exporting} className="rounded-md border border-forest/30 bg-forest-light/40 px-2.5 py-1 text-xs font-medium text-forest hover:bg-forest-light disabled:opacity-50" data-testid="export-xlsx-button">Excel</button>
            <button onClick={() => doExport("csv")} disabled={exporting} className="rounded-md border border-stone-200 px-2.5 py-1 text-xs font-medium text-stone-600 hover:bg-stone-50 disabled:opacity-50" data-testid="export-csv-button">CSV</button>
          </div>
        </div>
      </div>

      {isLoading || !data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
            <button
              onClick={() => setSelected(null)}
              data-testid="activity-card-total"
              className={`rounded-lg border p-3 text-left transition-all ${selected === null ? "border-forest ring-1 ring-forest bg-forest-light/60" : "border-forest/20 bg-forest-light/40 hover:border-forest/50"}`}
            >
              <div className="text-[11px] font-medium text-stone-500">Total activities</div>
              <div className="mt-1 font-heading text-2xl font-bold text-forest" data-testid="activity-total">{totals.total || 0}</div>
            </button>
            {ACT_META.map((m) => (
              <button
                key={m.key}
                onClick={() => setSelected((cur) => (cur === m.key ? null : m.key))}
                data-testid={`activity-card-${m.key}`}
                className={`rounded-lg border p-3 text-left transition-all ${selected === m.key ? "ring-2 bg-white" : "border-stone-200 bg-stone-50/60 hover:bg-white"}`}
                style={selected === m.key ? { borderColor: m.color, boxShadow: `0 0 0 1px ${m.color}` } : {}}
              >
                <div className="flex items-center gap-1 text-[11px] font-medium text-stone-500"><m.icon className="h-3.5 w-3.5" style={{ color: m.color }} /> {m.label}</div>
                <div className="mt-1 font-heading text-2xl font-bold text-stone-900">{totals[m.key] || 0}</div>
              </button>
            ))}
          </div>

          <div className="mt-5">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-400">Activity over time ({period}){selectedMeta ? ` · ${selectedMeta.label}` : ""}</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={series} margin={{ left: -20 }}>
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {ACT_META.filter((m) => !selected || m.key === selected).map((m) => (
                  <Bar key={m.key} dataKey={m.key} name={m.label} stackId="a" fill={m.color} radius={selected ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {selected && (
            <div className="mt-5" data-testid="activity-detail-panel">
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide" style={{ color: selectedMeta?.color }}>
                  {selectedMeta && <selectedMeta.icon className="h-3.5 w-3.5" />} {selectedMeta?.label} details
                </div>
                <button onClick={() => setSelected(null)} className="text-xs text-stone-400 hover:text-forest" data-testid="activity-detail-close">Clear ×</button>
              </div>
              {detailLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-forest" /></div>
              ) : (detail?.events || []).length === 0 ? (
                <div className="rounded-lg border border-dashed border-stone-300 py-8 text-center text-sm text-stone-400">No {selectedMeta?.label.toLowerCase()} in this period.</div>
              ) : (
                <div className="max-h-80 space-y-1.5 overflow-y-auto rounded-lg border border-stone-200 p-2">
                  {detail.events.map((e, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-stone-50" data-testid={`activity-event-${i}`}>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-stone-800">{e.customer_name}{e.company ? <span className="text-stone-400"> · {e.company}</span> : ""}</div>
                        <div className="truncate text-xs text-stone-500">{e.summary}</div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-xs font-medium text-stone-600">{fmtWhen(e.date)}</div>
                        {e.by && <div className="text-[11px] text-stone-400">{e.by}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-5">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-400">Work per customer</div>
            {data.per_customer.length === 0 ? (
              <div className="rounded-lg border border-dashed border-stone-300 py-10 text-center text-sm text-stone-400">No activity recorded in this period.</div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-stone-200">
                <table className="w-full text-sm" data-testid="activity-customer-table">
                  <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wide text-stone-400">
                    <tr>
                      <th className="px-3 py-2">Customer</th>
                      <th className="px-3 py-2">Assigned</th>
                      <th className="px-3 py-2 text-center">Msgs</th>
                      <th className="px-3 py-2 text-center">Resp.</th>
                      <th className="px-3 py-2 text-center">Follow-ups</th>
                      <th className="px-3 py-2 text-center">Stage</th>
                      <th className="px-3 py-2 text-center">Notes</th>
                      <th className="px-3 py-2 text-center">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.per_customer.map((pc) => (
                      <tr key={pc.customer_id} onClick={() => navigate(`/customers/${pc.customer_id}?tab=timeline`)} className="cursor-pointer border-t border-stone-100 hover:bg-forest-light/30" data-testid={`activity-row-${pc.customer_id}`}>
                        <td className="px-3 py-2.5 font-medium text-stone-800">{pc.name}{pc.company ? <span className="text-stone-400"> · {pc.company}</span> : ""}</td>
                        <td className="px-3 py-2.5 text-stone-500">{pc.assigned_name || "—"}</td>
                        <td className="px-3 py-2.5 text-center">{pc.messages_sent}</td>
                        <td className="px-3 py-2.5 text-center">{pc.responses}</td>
                        <td className="px-3 py-2.5 text-center">{pc.followups_completed}</td>
                        <td className="px-3 py-2.5 text-center">{pc.stage_changes}</td>
                        <td className="px-3 py-2.5 text-center">{pc.notes}</td>
                        <td className="px-3 py-2.5 text-center font-semibold text-forest">{pc.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mt-5">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-600">
              <AlertTriangle className="h-3.5 w-3.5" /> Idle customers — no activity this period
              {idle.length > 0 && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700" data-testid="idle-count">{idle.length}</span>}
            </div>
            {idle.length === 0 ? (
              <div className="rounded-lg border border-dashed border-emerald-200 bg-emerald-50/40 py-6 text-center text-sm text-emerald-700" data-testid="idle-empty">Great — every active customer in this scope had some activity.</div>
            ) : (
              <div className="max-h-72 space-y-1.5 overflow-y-auto rounded-lg border border-amber-200 bg-amber-50/30 p-2" data-testid="idle-customers-list">
                {idle.map((c) => (
                  <div key={c.customer_id} onClick={() => navigate(`/customers/${c.customer_id}?tab=timeline`)} className="flex cursor-pointer items-center justify-between gap-3 rounded-md bg-white px-2.5 py-1.5 hover:bg-forest-light/30" data-testid={`idle-row-${c.customer_id}`}>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-stone-800">{c.name}{c.company ? <span className="text-stone-400"> · {c.company}</span> : ""}</div>
                      <div className="text-[11px] text-stone-400">{(c.stage || "").replace(/_/g, " ")}{c.assigned_name ? ` · ${c.assigned_name}` : ""}</div>
                    </div>
                    <div className="shrink-0 text-right text-[11px] text-stone-400">
                      {c.last_response_at ? `Last reply ${fmtWhen(c.last_response_at)}` : "No reply yet"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function isoLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const pct = (done, target) => (target > 0 ? Math.min(100, Math.round((done / target) * 100)) : 0);

function DailyTasks() {
  const qc = useQueryClient();
  const [day, setDay] = useState(isoLocal(new Date()));
  const [editUser, setEditUser] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["daily-tasks", day],
    queryFn: async () => (await api.get("/daily-tasks", { params: { date: day } })).data,
  });
  const tasks = data?.tasks || [];

  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-white p-4 sm:p-5" data-testid="daily-tasks-section">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="flex items-center gap-2 font-heading font-semibold text-stone-900">
          <CheckCircle2 className="h-4 w-4 text-forest" /> Daily tasks
        </h3>
        <div className="flex items-center gap-2 text-xs text-stone-500">
          <span>Date</span>
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="h-8 rounded-lg border border-stone-200 bg-white px-2 text-xs text-stone-700" data-testid="daily-task-date-input" />
        </div>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {tasks.map((t) => {
            const p = t.message_target > 0 ? Math.min(100, Math.round((t.messages_sent / t.message_target) * 100)) : 0;
            return (
              <div key={t.user_id} className="rounded-xl border border-stone-200 p-3.5" data-testid={`daily-task-card-${t.user_id}`}>
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <div className="font-heading text-sm font-semibold text-stone-900">{t.name}</div>
                    <div className="text-[11px] text-stone-400">{t.role === "manager" ? "Manager" : "Executive"}</div>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditUser(t)} data-testid={`daily-task-edit-${t.user_id}`}><Pencil className="mr-1 h-3 w-3" /> Set task</Button>
                </div>
                {!t.message_target ? (
                  <div className="rounded-lg bg-stone-50 py-3 text-center text-xs text-stone-400">No task set for this day.</div>
                ) : (
                  <>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="text-stone-500">Messages to send</span>
                      <span className="font-semibold text-stone-800" data-testid={`daily-task-progress-${t.user_id}`}>{t.messages_sent} / {t.message_target}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                      <div className="h-full rounded-full bg-forest transition-all" style={{ width: `${p}%` }} />
                    </div>
                    {t.note && <p className="mt-2 text-xs text-stone-500">{t.note}</p>}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      {editUser && (
        <SetDailyTaskDialog
          task={editUser}
          day={day}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); qc.invalidateQueries({ queryKey: ["daily-tasks"] }); }}
        />
      )}
    </div>
  );
}

function SetDailyTaskDialog({ task, day, onClose, onSaved }) {
  const [target, setTarget] = useState(task.message_target || "");
  const [note, setNote] = useState(task.note || "");
  const [loading, setLoading] = useState(false);
  const save = async () => {
    setLoading(true);
    try {
      await api.post("/daily-tasks", {
        user_id: task.user_id, date: day,
        message_target: Number(target) || 0, note: note || null,
      });
      toast.success(`Task set for ${task.name}`);
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm" data-testid="set-daily-task-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Daily task · {task.name}</DialogTitle>
          <DialogDescription>Set the number of messages to send on {day}. The executive sees this and is reminded every hour.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Messages to send (count)</Label>
            <Input type="number" min="0" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. 50" data-testid="daily-task-target-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Focus on Stage-2 prospects" data-testid="daily-task-note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={save} disabled={loading} data-testid="daily-task-save">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save task"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DailyReportsView() {
  const [day, setDay] = useState(isoLocal(new Date()));
  const [salesperson, setSalesperson] = useState("all");
  const [openId, setOpenId] = useState(null);

  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const salespeople = users.filter((u) => u.role === "executive" || u.role === "manager");

  const { data, isLoading } = useQuery({
    queryKey: ["daily-reports", day, salesperson],
    queryFn: async () => {
      const params = { date: day };
      if (salesperson !== "all") params.salesperson_id = salesperson;
      return (await api.get("/daily-reports", { params })).data;
    },
  });
  const reports = data?.reports || [];

  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-white p-4 sm:p-5" data-testid="daily-reports-section">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="flex items-center gap-2 font-heading font-semibold text-stone-900">
          <ClipboardCheck className="h-4 w-4 text-forest" /> Daily reports
          {data && <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-normal text-stone-500" data-testid="reports-submitted-count">{data.submitted_count}/{data.total} submitted</span>}
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={salesperson} onValueChange={setSalesperson}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="daily-reports-salesperson"><SelectValue placeholder="Salesperson" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All salespeople</SelectItem>
              {salespeople.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="h-8 rounded-lg border border-stone-200 bg-white px-2 text-xs text-stone-700" data-testid="daily-reports-date" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>
      ) : (
        <div className="space-y-2">
          {reports.map((r) => {
            const s = r.snapshot;
            const metrics = [
              ["Msgs", `${s.messages.done}/${s.messages.target || "—"}`],
              ["Conv today", s.today.conversions],
              ["Rev today", rupee(s.today.revenue)],
              ["Calls", r.calls_made],
              ["Samples", r.samples_dispatched],
              ["Meetings", r.meetings],
              ["Quotes", r.quotations_shared],
              ["New leads", s.activity.new_customers],
            ];
            const hasText = r.summary || r.blockers || r.plan_tomorrow;
            return (
              <div key={r.user_id} className="rounded-xl border border-stone-200" data-testid={`daily-report-${r.user_id}`}>
                <button onClick={() => setOpenId(openId === r.user_id ? null : r.user_id)} className="flex w-full items-center justify-between gap-3 p-3 text-left">
                  <div className="flex items-center gap-2">
                    <div className="font-heading text-sm font-semibold text-stone-900">{r.name}</div>
                    <span className="text-[10px] uppercase text-stone-400">{r.role === "manager" ? "Mgr" : "Exec"}</span>
                    {r.submitted
                      ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700" data-testid={`report-badge-${r.user_id}`}>Submitted</span>
                      : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700" data-testid={`report-badge-${r.user_id}`}>Not submitted</span>}
                  </div>
                  <div className="hidden gap-3 text-xs text-stone-500 sm:flex">
                    <span>Msgs <b className="text-stone-800">{s.messages.done}/{s.messages.target || "—"}</b></span>
                    <span>Rev <b className="text-stone-800">{rupee(s.today.revenue)}</b></span>
                  </div>
                </button>
                {openId === r.user_id && (
                  <div className="border-t border-stone-100 p-3">
                    <div className="grid grid-cols-4 gap-2 sm:grid-cols-8">
                      {metrics.map(([l, v]) => (
                        <div key={l} className="rounded-lg bg-stone-50 p-2 text-center">
                          <div className="font-heading text-sm font-bold text-stone-800">{v}</div>
                          <div className="text-[9px] text-stone-400">{l}</div>
                        </div>
                      ))}
                    </div>
                    {hasText ? (
                      <div className="mt-3 space-y-2 text-sm">
                        {r.summary && <div><span className="text-xs font-semibold text-stone-500">Accomplished: </span><span className="text-stone-700">{r.summary}</span></div>}
                        {r.blockers && <div><span className="text-xs font-semibold text-amber-600">Blockers: </span><span className="text-stone-700">{r.blockers}</span></div>}
                        {r.plan_tomorrow && <div><span className="text-xs font-semibold text-forest">Tomorrow: </span><span className="text-stone-700">{r.plan_tomorrow}</span></div>}
                      </div>
                    ) : (
                      <div className="mt-3 text-xs text-stone-400">{r.submitted ? "No written notes." : "Report not submitted for this day."}</div>
                    )}
                    {r.submitted_at && <div className="mt-2 text-[10px] text-stone-400">Submitted {new Date(r.submitted_at).toLocaleString("en-IN")}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GoalTargets() {
  const qc = useQueryClient();
  const [weekRef, setWeekRef] = useState(isoLocal(new Date()));
  const [editUser, setEditUser] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["goals", weekRef],
    queryFn: async () => (await api.get("/goals", { params: { week_start: weekRef } })).data,
  });
  const goals = data?.goals || [];

  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-white p-4 sm:p-5" data-testid="goals-section">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="flex items-center gap-2 font-heading font-semibold text-stone-900">
          <Target className="h-4 w-4 text-forest" /> Weekly goal targets
        </h3>
        <div className="flex items-center gap-2 text-xs text-stone-500">
          <span>Week of</span>
          <input type="date" value={weekRef} onChange={(e) => setWeekRef(e.target.value)} className="h-8 rounded-lg border border-stone-200 bg-white px-2 text-xs text-stone-700" data-testid="goals-week-input" />
          {data && <span className="rounded-md bg-stone-100 px-2 py-1 text-[11px] text-stone-600" data-testid="goals-week-range">{data.week_start} → {data.week_end}</span>}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {goals.map((g) => {
            const convPct = pct(g.conversions_done, g.conversions_target);
            const revPct = pct(g.revenue_done, g.revenue_target);
            const noTarget = !g.conversions_target && !g.revenue_target;
            return (
              <div key={g.user_id} className="rounded-xl border border-stone-200 p-3.5" data-testid={`goal-card-${g.user_id}`}>
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <div className="font-heading text-sm font-semibold text-stone-900">{g.name}</div>
                    <div className="text-[11px] text-stone-400">{g.role === "manager" ? "Manager" : "Executive"}{!g.target_is_explicit && !noTarget ? " · carried forward" : ""}</div>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditUser(g)} data-testid={`goal-edit-${g.user_id}`}><Pencil className="mr-1 h-3 w-3" /> Set target</Button>
                </div>
                {noTarget ? (
                  <div className="rounded-lg bg-stone-50 py-4 text-center text-xs text-stone-400">No target set for this week.</div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <div className="mb-1 flex items-center justify-between text-xs">
                        <span className="text-stone-500">Prospect conversions</span>
                        <span className="font-semibold text-stone-800" data-testid={`goal-conv-${g.user_id}`}>{g.conversions_done} / {g.conversions_target || "—"}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                        <div className="h-full rounded-full bg-forest transition-all" style={{ width: `${convPct}%` }} />
                      </div>
                    </div>
                    <div>
                      <div className="mb-1 flex items-center justify-between text-xs">
                        <span className="text-stone-500">Existing revenue</span>
                        <span className="font-semibold text-stone-800" data-testid={`goal-rev-${g.user_id}`}>{rupee(g.revenue_done)} / {g.revenue_target ? rupee(g.revenue_target) : "—"}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                        <div className="h-full rounded-full bg-gold transition-all" style={{ width: `${revPct}%` }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editUser && (
        <SetGoalDialog
          user={editUser}
          weekRef={weekRef}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); qc.invalidateQueries({ queryKey: ["goals"] }); }}
        />
      )}
    </div>
  );
}

function SetGoalDialog({ user, weekRef, onClose, onSaved }) {
  const [conv, setConv] = useState(user.conversions_target || "");
  const [rev, setRev] = useState(user.revenue_target || "");
  const [loading, setLoading] = useState(false);
  const save = async () => {
    setLoading(true);
    try {
      await api.post("/goals", {
        user_id: user.user_id,
        week_start: weekRef,
        conversions_target: Number(conv) || 0,
        revenue_target: Number(rev) || 0,
      });
      toast.success(`Target set for ${user.name}`);
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm" data-testid="set-goal-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Target className="h-4 w-4" /> Weekly target · {user.name}</DialogTitle>
          <DialogDescription>Applies to the selected week and carries forward until you change it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Prospect conversions (count)</Label>
            <Input type="number" min="0" value={conv} onChange={(e) => setConv(e.target.value)} placeholder="e.g. 3" data-testid="goal-conversions-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Existing-customer revenue (₹)</Label>
            <Input type="number" min="0" value={rev} onChange={(e) => setRev(e.target.value)} placeholder="e.g. 100000" data-testid="goal-revenue-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={save} disabled={loading} data-testid="goal-save">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save target"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const REV_COLORS = ["#14532D", "#B45309", "#155E75", "#10B981", "#D97706", "#7C3AED", "#DC2626", "#0EA5E9", "#CA8A04", "#065F46"];

function RevenueByExecutive() {
  const [weeks, setWeeks] = useState(8);
  const [focus, setFocus] = useState("all");
  const { data, isLoading } = useQuery({
    queryKey: ["revenue-timeseries", weeks],
    queryFn: async () => (await api.get("/reports/revenue-timeseries", { params: { weeks } })).data,
  });
  const rows = data?.rows || [];
  const names = data?.names || [];
  const totals = data?.totals || [];
  const shown = focus === "all" ? names : names.filter((n) => n === focus);
  const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
  const hasData = totals.some((t) => t.revenue > 0);

  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-white p-4 sm:p-5" data-testid="revenue-timeseries-section">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="flex items-center gap-2 font-heading font-semibold text-stone-900">
          <IndianRupee className="h-4 w-4 text-gold" /> Revenue by executive
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={focus} onValueChange={setFocus}>
            <SelectTrigger className="h-9 w-44 bg-white" data-testid="revenue-focus-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All salespeople</SelectItem>
              {names.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex rounded-lg border border-stone-200 p-0.5" data-testid="revenue-weeks-toggle">
            {[[8, "8w"], [12, "12w"], [26, "26w"]].map(([v, label]) => (
              <button key={v} onClick={() => setWeeks(v)} data-testid={`revenue-weeks-${v}`}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${weeks === v ? "bg-forest text-ivory" : "text-stone-500 hover:text-forest"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>
      ) : !hasData ? (
        <div className="rounded-lg border border-dashed border-stone-300 bg-stone-50/50 py-14 text-center text-sm text-stone-400" data-testid="revenue-empty">
          No confirmed-order revenue recorded in this period yet.
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={rows} margin={{ left: 4, right: 8, top: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0eee8" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} width={44} />
              <Tooltip formatter={(v, n) => [inr(v), n]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {shown.map((n) => (
                <Line key={n} type="monotone" dataKey={n} stroke={REV_COLORS[names.indexOf(n) % REV_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
              ))}
              {focus !== "all" && (
                <Line key={`${focus}-target`} type="monotone" dataKey={`${focus} target`} name="Target" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 4" dot={false} />
              )}
            </LineChart>
          </ResponsiveContainer>

          <div className="mt-4 flex flex-wrap gap-2" data-testid="revenue-totals">
            {totals.filter((t) => t.revenue > 0 || t.weekly_target > 0).map((t) => {
              const met = t.this_week >= t.weekly_target;
              return (
                <span key={t.user_id} className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-stone-50 px-2.5 py-1 text-xs" data-testid={`revenue-chip-${t.user_id}`}>
                  <span className="font-medium text-stone-700">{t.name}</span>
                  <span className="font-semibold text-forest">{inr(t.revenue)}</span>
                  {t.weekly_target > 0 && (
                    <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${met ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`} title={`This week ${inr(t.this_week)} vs target ${inr(t.weekly_target)}`}>
                      {met ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      this wk {inr(t.this_week)}/{inr(t.weekly_target)}
                    </span>
                  )}
                </span>
              );
            })}
          </div>
          <p className="mt-3 text-[11px] text-stone-400">Weekly total of confirmed-order value, credited to each customer's assigned salesperson. Focus on one salesperson to overlay their weekly target (dashed line).</p>
        </>
      )}
    </div>
  );
}

function Leaderboard() {
  const [range, setRange] = useState("week");
  const bounds = () => {
    const now = new Date();
    if (range === "month") {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: isoLocal(s), end: isoLocal(now) };
    }
    if (range === "all") {
      return { start: "2000-01-01", end: isoLocal(now) };
    }
    const day = (now.getDay() + 6) % 7; // Monday=0
    const s = new Date(now); s.setDate(now.getDate() - day);
    const e = new Date(s); e.setDate(s.getDate() + 6);
    return { start: isoLocal(s), end: isoLocal(e) };
  };

  const { data, isLoading } = useQuery({
    queryKey: ["leaderboard", range],
    queryFn: async () => (await api.get("/reports/leaderboard", { params: bounds() })).data,
  });
  const rows = data?.leaderboard || [];

  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-white p-4 sm:p-5" data-testid="leaderboard-section">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="flex items-center gap-2 font-heading font-semibold text-stone-900">
          <Trophy className="h-4 w-4 text-gold" /> Executive leaderboard
        </h3>
        <div className="flex rounded-lg border border-stone-200 p-0.5" data-testid="leaderboard-range-toggle">
          {[["week", "This week"], ["month", "This month"], ["all", "All time"]].map(([v, label]) => (
            <button key={v} onClick={() => setRange(v)} data-testid={`leaderboard-range-${v}`}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${range === v ? "bg-forest text-ivory" : "text-stone-500 hover:text-forest"}`}>{label}</button>
          ))}
        </div>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-stone-200">
          <table className="w-full text-sm" data-testid="leaderboard-table">
            <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wide text-stone-400">
              <tr>
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">Executive</th>
                <th className="px-3 py-2 text-center">Assigned</th>
                <th className="px-3 py-2 text-center">Orders confirmed</th>
                <th className="px-3 py-2 text-center">Conversion %</th>
                <th className="px-3 py-2 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.user_id} className={`border-t border-stone-100 ${i === 0 && r.orders_confirmed > 0 ? "bg-gold/5" : ""}`} data-testid={`leaderboard-row-${r.user_id}`}>
                  <td className="px-3 py-2.5"><span className={`grid h-6 w-6 place-items-center rounded-full text-xs font-bold ${i === 0 && r.orders_confirmed > 0 ? "bg-gold text-white" : "bg-stone-100 text-stone-500"}`}>{i + 1}</span></td>
                  <td className="px-3 py-2.5 font-medium text-stone-800">{r.name}<span className="ml-1 text-[10px] uppercase text-stone-400">{r.role === "manager" ? "Mgr" : "Exec"}</span></td>
                  <td className="px-3 py-2.5 text-center text-stone-600">{r.assigned}</td>
                  <td className="px-3 py-2.5 text-center font-semibold text-forest">{r.orders_confirmed}</td>
                  <td className="px-3 py-2.5 text-center text-stone-700">{r.conversion}%</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-stone-900">{rupee(r.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <div className="py-8 text-center text-sm text-stone-400">No data for this period.</div>}
        </div>
      )}
      <p className="mt-2 text-[11px] text-stone-400">Orders & revenue come from manually recorded "Order confirmed" responses in the period. Conversion % = converted customers ÷ assigned customers.</p>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4">
      <div className="flex items-center gap-1.5 text-xs font-medium text-stone-500"><Icon className="h-4 w-4" /> {label}</div>
      <div className={`mt-1.5 font-heading text-2xl font-bold text-stone-900 ${tone || ""}`}>{value}</div>
    </div>
  );
}

function Card({ title, children, className = "" }) {
  return (
    <div className={`rounded-xl border border-stone-200 bg-white p-4 sm:p-5 ${className}`}>
      <h3 className="mb-3 font-heading font-semibold text-stone-900">{title}</h3>
      {children}
    </div>
  );
}

function Conv({ label, value }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4">
      <div className="flex items-center gap-1.5 text-xs font-medium text-stone-500"><TrendingUp className="h-4 w-4 text-forest" /> {label}</div>
      <div className="mt-2 font-heading text-3xl font-bold text-forest">{value}%</div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-stone-100">
        <div className="h-full rounded-full bg-gold" style={{ width: `${Math.min(value, 100)}%` }} />
      </div>
    </div>
  );
}
